import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  isTerminalStatus,
  useBackgroundTasks,
  useTrackedTasks,
} from '@tyro/api';
import { ActionMenu } from '@tyro/core/action-menu';
import {
  DialogCloseButton,
  DialogContent,
  DialogTitle,
} from '@tyro/core/dialog';
import {
  FloatCardDraggableContainer,
  FloatingCard,
  useFloatCardPositioning,
} from '@tyro/core/floating-card';
import { useTranslation } from '@tyro/i18n';
import {
  CollapseIcon,
  DocListIcon,
  ExpandIcon,
  ExternalLinkIcon,
  VerticalDotsIcon,
} from '@tyro/icons';
import { useNavigate } from '@tyro/router/hooks';
import { AnimatePresence, m } from 'motion/react';
import { useMemo } from 'react';
import { formatHandlerName } from '../../utils/format-handler-name';
import { TaskStatusChip } from './task-status-chip';

const EXPANDED_WIDTH = 380;
const EXPANDED_HEIGHT = 400;
const MINIMISED_HEIGHT = 48;

function BackgroundTasksFloatingCardContent() {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const { closePopup, trackedIds } = useBackgroundTasks();
  const { setSize, height } = useFloatCardPositioning();

  const executionIds = useMemo(() => Array.from(trackedIds), [trackedIds]);
  const { data: tasks = [] } = useTrackedTasks(executionIds);

  const completedCount = tasks.filter((task) =>
    isTerminalStatus(task.status),
  ).length;
  const totalCount = tasks.length;

  const isMinimised = height <= MINIMISED_HEIGHT;

  return (
    <>
      <FloatCardDraggableContainer>
        <Stack
          direction="row"
          p={1}
          pl={1.5}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="divider"
        >
          <Stack direction="row" alignItems="center" spacing={1} flex={1}>
            <DialogTitle containerProps={{ p: 0, pr: 0 }}>
              {t('common:backgroundTasks.title')}
            </DialogTitle>
            {totalCount > 0 && (
              <Chip
                label={`${completedCount}/${totalCount}`}
                size="small"
                color={completedCount === totalCount ? 'success' : 'info'}
                variant="soft"
              />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <ActionMenu
              iconOnly
              buttonIcon={
                <VerticalDotsIcon
                  sx={{
                    width: 20,
                    height: 20,
                    '& g': {
                      strokeWidth: 1.8,
                    },
                  }}
                />
              }
              buttonProps={{
                sx: {
                  width: 30,
                  height: 30,
                },
              }}
              menuItems={[
                {
                  label: isMinimised
                    ? t('common:expand')
                    : t('common:collapse'),
                  icon: isMinimised ? <ExpandIcon /> : <CollapseIcon />,
                  onClick: () => {
                    setSize({
                      height: isMinimised ? EXPANDED_HEIGHT : MINIMISED_HEIGHT,
                    });
                  },
                },
                {
                  label: t('common:backgroundTasks.viewAll'),
                  icon: <DocListIcon />,
                  onClick: () => {
                    navigate('/tasks');
                  },
                },
              ]}
            />
            <DialogCloseButton
              onClick={closePopup}
              sx={{ position: 'static' }}
            />
          </Stack>
        </Stack>
      </FloatCardDraggableContainer>

      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {tasks.length === 0 ? (
          <Stack
            flex={1}
            alignItems="center"
            justifyContent="center"
            spacing={1}
            py={4}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              {t('common:backgroundTasks.loading')}
            </Typography>
          </Stack>
        ) : (
          <Stack>
            <AnimatePresence initial={false}>
              {tasks.map((task, index) => {
                const linkResults = task.taskResults.filter(
                  (result) => result.type === 'LINK',
                );
                const messageResults = task.taskResults.filter(
                  (result) => result.type === 'MESSAGE',
                );
                return (
                  <m.div
                    key={task.executionId}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.2 }}
                    style={{ overflow: 'hidden' }}
                  >
                    {index > 0 && (
                      <Box
                        sx={{
                          borderTop: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    )}
                    <Stack spacing={0.5} px={2} py={1.5}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          noWrap
                          minWidth={0}
                        >
                          {formatHandlerName(task.handlerName)}
                        </Typography>
                        <TaskStatusChip
                          status={task.status}
                          exceptionMessage={task.exceptionMessage}
                        />
                      </Stack>
                      {messageResults.map((result) => (
                        <Typography
                          key={result.payload}
                          variant="caption"
                          color="text.secondary"
                        >
                          {result.title ?? result.payload}
                        </Typography>
                      ))}
                      {linkResults.map((result) => (
                        <Link
                          key={result.payload}
                          href={result.payload}
                          target="_blank"
                          rel="noreferrer"
                          variant="caption"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {result.title ??
                            t('common:backgroundTasks.viewResult')}
                          <ExternalLinkIcon
                            sx={{
                              width: 12,
                              height: 12,
                              '& g': { strokeWidth: 2 },
                            }}
                          />
                        </Link>
                      ))}
                    </Stack>
                  </m.div>
                );
              })}
            </AnimatePresence>
          </Stack>
        )}
      </DialogContent>
    </>
  );
}

export function BackgroundTasksFloatingCard() {
  const { isPopupOpen, closePopup } = useBackgroundTasks();

  return (
    <FloatingCard
      open={isPopupOpen}
      onClose={closePopup}
      initialPosition={{ bottom: 16, right: 88 }}
      initialWidth={EXPANDED_WIDTH}
      initialHeight={EXPANDED_HEIGHT}
      CardProps={{
        sx: { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
      }}
    >
      <BackgroundTasksFloatingCardContent />
    </FloatingCard>
  );
}
